import { useMemo } from "react";
import { Share2, MessageCircle, Twitter, Copy } from "lucide-react";
import { toast } from "sonner";

const SITE = "blendsafe.com";
const SITE_URL = "https://blendsafe.com";

function bucket(result) {
  const v = (result?.verdict || result?.severity_tier || result?.overall_result || "").toUpperCase();
  if (v === "AVOID") return "AVOID";
  if (v === "CAUTION") return "CAUTION";
  return "SAFE";
}

/**
 * Build the canonical BlendSafe share URL that crawlers (Twitter, WhatsApp,
 * Slack, Discord, Telegram, iMessage, Reddit) unfurl into a verdict-specific
 * 1200×630 OG card.
 *
 * Preferred shape (per-check): `https://blendsafe.com/api/share/{share_id}`
 * Fallback shape (query-param): `https://blendsafe.com/api/og/preview?med=&product=&verdict=&score=`
 *
 * Both routes return server-rendered HTML carrying og:image / twitter:image
 * and a JS redirect → `/check?med=&product=` for human visitors.
 */
function buildShareUrl(result) {
  // Prefer the canonical share_id (covers all crawlers + privacy contract).
  const sid = result?.share_id || result?.history_id || result?.check_history_id;
  if (sid) {
    return `${SITE_URL}/api/share/${sid}`;
  }
  // Query-param fallback for results that weren't persisted server-side.
  const med0 = result?.medication
    || (result?.medications && result.medications[0])
    || null;
  const params = new URLSearchParams();
  if (med0?.id) params.set("med", med0.id);
  if (result?.product_id) {
    params.set("product", result.product_id);
  } else if (result?.product_name) {
    // Slugify just enough for the catalog probe; server falls back to
    // humanized text when slug doesn't resolve.
    const slug = String(result.product_name)
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/^-+|-+$/g, "")
      .slice(0, 80);
    if (slug) params.set("product", slug);
  }
  const v = bucket(result);
  params.set("verdict", v);
  if (typeof result?.safety_score === "number") {
    params.set("score", String(result.safety_score));
  }
  return `${SITE_URL}/api/og/preview?${params.toString()}`;
}

function buildMessages(result) {
  const med = result?.medication?.name
    || result?.medication?.generic_name
    || (result?.medications && result.medications[0]?.name)
    || "your medication";
  const product = result?.product_name || "the product";
  const b = bucket(result);
  if (b === "AVOID") {
    return {
      heading: "Warn a friend",
      sub: "Share this result before they make the same mistake.",
      body: `I just found out ${med} + ${product} = AVOID. Check your routine free at ${SITE}`,
      kind: "AVOID",
    };
  }
  if (b === "CAUTION") {
    return {
      heading: "Give a friend a heads-up",
      sub: "This combo needs caution — share before they layer them.",
      body: `Heads-up: ${med} + ${product} = CAUTION. Quick free check at ${SITE}`,
      kind: "CAUTION",
    };
  }
  return {
    heading: "Pass on the peace of mind",
    sub: "Share this result with someone who was worried.",
    body: `I checked ${med} + ${product} on ${SITE} — no label conflict found. Free skincare × medication check, no signup.`,
    kind: "SAFE",
  };
}

/**
 * ShareResultPrompt — engagement-driving share strip below the result card.
 * Shows three native share targets: WhatsApp, Twitter/X, and Copy. Each
 * pre-fills a verdict-aware message with the medication + product name and
 * the OG-card-bearing share URL that unfurls into a beautiful preview on
 * every major platform.
 */
export default function ShareResultPrompt({ result }) {
  const msg = useMemo(() => buildMessages(result), [result]);
  const shareUrl = useMemo(() => buildShareUrl(result), [result]);

  if (!result || !msg) return null;

  // Compose the user-facing text: pre-filled body + the BlendSafe share URL
  // that crawlers will unfurl.
  const textPayload = `${msg.body}\n${shareUrl}`;
  const encodedText = encodeURIComponent(textPayload);
  const encodedTweet = encodeURIComponent(msg.body);
  const whatsapp = `https://wa.me/?text=${encodedText}`;
  const twitter = `https://twitter.com/intent/tweet?text=${encodedTweet}&url=${encodeURIComponent(shareUrl)}`;

  const copy = async () => {
    try {
      await navigator.clipboard?.writeText?.(textPayload);
      toast.success("Copied to clipboard", {
        description: "Paste it anywhere — group chat, email, or DM.",
      });
    } catch {
      toast.error("Couldn't copy", { description: "Long-press to copy manually." });
    }
  };

  return (
    <section
      data-testid="share-result-prompt"
      data-share-kind={msg.kind}
      data-share-url={shareUrl}
      className="mt-6 rounded-3xl border bg-white p-5 sm:p-6 animate-fade-up"
      style={{
        borderColor: "rgba(31,61,46,0.10)",
        boxShadow: "0 1px 2px rgba(0,0,0,0.03), 0 8px 24px -16px rgba(0,0,0,0.10)",
      }}
    >
      <div className="flex items-start gap-3">
        <span
          className="inline-flex items-center justify-center w-10 h-10 rounded-2xl shrink-0"
          style={{ background: "rgba(31,61,46,0.06)", color: "#1f3d2e" }}
        >
          <Share2 className="w-5 h-5" />
        </span>
        <div className="flex-1 min-w-0">
          <h3 className="text-[16px] sm:text-[17px] font-extrabold text-ink leading-snug">
            {msg.heading}
          </h3>
          <p className="mt-0.5 text-[13px] text-ink-mute leading-snug">{msg.sub}</p>
        </div>
      </div>

      <div className="mt-4 grid grid-cols-1 sm:grid-cols-3 gap-2.5">
        <a
          href={whatsapp}
          target="_blank"
          rel="noopener noreferrer"
          data-testid="share-whatsapp"
          className="inline-flex items-center justify-center gap-2 h-11 rounded-xl text-cream-50 text-[13.5px] font-semibold transition-all hover:scale-[1.01]"
          style={{ background: "#25D366" }}
        >
          <MessageCircle className="w-4 h-4" />
          WhatsApp
        </a>
        <a
          href={twitter}
          target="_blank"
          rel="noopener noreferrer"
          data-testid="share-twitter"
          className="inline-flex items-center justify-center gap-2 h-11 rounded-xl text-cream-50 text-[13.5px] font-semibold transition-all hover:scale-[1.01]"
          style={{ background: "#0f1419" }}
        >
          <Twitter className="w-4 h-4" />
          Tweet combo
        </a>
        <button
          type="button"
          onClick={copy}
          data-testid="share-copy"
          className="inline-flex items-center justify-center gap-2 h-11 rounded-xl bg-cream-50 border border-surface-line text-ink text-[13.5px] font-semibold transition-all hover:bg-cream-100 hover:scale-[1.01]"
        >
          <Copy className="w-4 h-4" />
          Copy
        </button>
      </div>

      <p className="mt-3 text-[11.5px] text-ink-mute">
        Shared link unfurls into a {bucket(result).toLowerCase()}-verdict preview on
        WhatsApp, Twitter, Discord, Slack and iMessage.
      </p>
    </section>
  );
}
